import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, useAnimations, PerspectiveCamera } from '@react-three/drei';
import { gsap } from 'gsap'; // Import GSAP for animations
import { motion } from 'framer-motion'; // Import motion from Framer Motion
import { Icon } from '@iconify/react/dist/iconify.js';
import Spline from '@splinetool/react-spline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import nsabha1 from './Assets/nsabhaHomepage.png'
import nsabha2 from './Assets/Screenshot 2024-10-03 135400.png'
import hash1 from './Assets/Screenshot 2024-10-03 140757.png'
import hash2 from './Assets/Screenshot 2024-10-03 140757.png'
import useScreenSize from './useScreenSize';
import AOS from 'aos'; // Import AOS library

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  height:'500px',
  bgcolor: '#080808',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

// Model component that takes url and rotation as props
function Model({ url, targetRotation, rotationSpeed }) {
  const group = useRef();
  const { scene, animations } = useGLTF(url); // Load the GLTF/GLB model from the provided URL
  const { actions } = useAnimations(animations, group); // Initialize animations

  // Rotate the model by updating the rotation using useFrame
  useFrame(() => {
    if (group.current) {
      // Smoothly interpolate towards the target rotation using lerp
      group.current.rotation.y += (targetRotation - group.current.rotation.y) * rotationSpeed.current;
    }
  });

  // Play the first animation (if available) when the component mounts
  useEffect(() => {
    if (actions) {
      const action = actions[Object.keys(actions)[0]]; // Get the first animation
      if (action) {
        action.play(); // Play the animation
      }
    }
  }, [actions]);

  return <primitive ref={group} object={scene} scale={1} />;
}

// Main Projects component
export default function Projects({ modelUrl }) {
  const { width, height } = useScreenSize();
  const [targetRotation, setTargetRotation] = useState(0);
  const [whichProject, setWhichProject] = useState(0);
  const cameraRef = useRef(); // Ref to access the camera
  const initialCameraPosition = useRef([0, 0, 1]); // Initial camera position closer to the model (more zoomed in)
  const rotationSpeed = useRef(0.0002); // Initial slow rotation speed
  const [nextButtonShow, setNextButtonShow] = useState(true);
  const [projectVisible, setProjectVisible] = useState(true); // State to control visibility of project divs
  
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const[isReadMore,setIsReadMore]=useState(true)
   // Initialize AOS when the component mounts
   useEffect(() => {
    AOS.init({
      duration: 1000, // Animation duration in milliseconds
      once: true, // Whether animation should happen only once while scrolling down
    });
  }, []);
  // Project titles for each project
  const projectTitles = [
    {
      title: 'Webcasting Project',
      desc: 'The Kerala Legislative Assembly webcasting project aims to make legislative video content easily accessible to everyone, providing a seamless and user-friendly experience for viewing live sessions and related content.',
      img: [nsabha1,nsabha2],
      rdDesc:'The Kerala Legislative Assembly webcasting project aims to make legislative video content easily accessible to everyone, providing a seamless and user-friendly experience for viewing live sessions and related content. The project features a dynamic video player that supports live streaming and on-demand playback, ensuring users can access legislative proceedings in real-time or at their convenience. Additionally, it includes an admin module for efficient management of members, events, and content. This module allows administrators to manage video uploads, schedule live sessions, and control access permissions. The platform is designed to enhance user engagement by offering features such as searchable video archives, categorization of content by topics, and integration with other digital resources, making legislative information more organized and readily available to the public. Overall, the project aims to promote transparency and accessibility in the legislative process through advanced technology.'
    },
    {
      title: 'Lawyers Case Management',
      desc: 'focused on providing digital solutions for lawyers. The goal of the project was to streamline case management, improve client interactions, and simplify legal processes through an efficient, user-friendly platform.',
      img: ['https://images.pexels.com/photos/1714341/pexels-photo-1714341.jpeg?auto=compress&cs=tinysrgb&w=600','https://images.pexels.com/photos/943096/pexels-photo-943096.jpeg?auto=compress&cs=tinysrgb&w=600'],
      rdDesc:'The project at Hashraft focuses on providing innovative digital solutions tailored for lawyers and legal professionals. Its primary goal is to streamline case management, improve client interactions, and simplify legal processes through an efficient, user-friendly platform.Key features of the platform include comprehensive case management tools that enable lawyers to track case progress, manage deadlines, and organize case-related documents for easy access. It also offers secure document storage solutions, ensuring that legal documents are safely stored and easily retrievable while maintaining confidentiality and compliance.Additionally, the platform includes integrated communication tools that facilitate direct interaction with clients, such as messaging systems and appointment scheduling, along with client portals for easy access to case updates. Reporting and analytics features help firms generate reports and analyze case data, enabling informed decision-making and improved efficiency.By focusing on these areas, the project aims to reduce administrative burdens, enhance collaboration, and provide a seamless experience for both legal professionals and their clients, ultimately making legal work more organized and efficient.'
    },
    {
      title: '3D Interior Design',
      desc: 'The project involved developing a 3D interior design software that features 3D room models and furniture placement, enabling users to visualize and arrange interior spaces effectively.',
      img: ['https://images.pexels.com/photos/2102416/pexels-photo-2102416.jpeg?auto=compress&cs=tinysrgb&w=600','https://images.pexels.com/photos/943096/pexels-photo-943096.jpeg?auto=compress&cs=tinysrgb&w=600'],
      rdDesc:'The project involved developing a 3D interior design software that includes realistic 3D room models and furniture placement capabilities. This software enables users to effectively visualize and arrange interior spaces, allowing them to experiment with different layouts and design styles. With intuitive tools and features, users can drag and drop furniture, adjust dimensions, and create customized room settings, making the design process both engaging and efficient. The platform aims to empower users to bring their interior design ideas to life, providing a creative and practical solution for personal or professional use.'
    },
    {
      title: 'Garagex',
      desc: 'GarageX is a platform where people can buy and sell used bikes, facilitating easy transactions and connecting buyers and sellers in the motorcycle market.',
      img: ['https://images.pexels.com/photos/572056/pexels-photo-572056.jpeg?auto=compress&cs=tinysrgb&w=600','https://images.pexels.com/photos/943096/pexels-photo-943096.jpeg?auto=compress&cs=tinysrgb&w=600'],
      rdDesc:'GarageX is a platform designed for buying and selling used bikes, providing a streamlined and user-friendly experience for both buyers and sellers. The platform facilitates easy transactions by connecting individuals in the motorcycle market, allowing users to browse a wide selection of available bikes, compare prices, and communicate directly with sellers.With features such as detailed listings, user reviews, and secure payment options, GarageX aims to create a trusted environment for users to make informed decisions. Additionally, the platform supports sellers in showcasing their bikes effectively, making it easier to reach potential buyers. Overall, GarageX seeks to enhance the buying and selling experience in the used motorcycle market, promoting accessibility and convenience for all users.'
    },
  ];

  // Function to rotate the model by 270 degrees (in radians) on button click
  const handleButtonClick = () => {
    if (!cameraRef.current) return; // Ensure cameraRef is defined
    setNextButtonShow(false);
    setProjectVisible(false); // Hide project divs during zoom-out

    const originalPositionZ = cameraRef.current.position.z; // Store original camera position

    // Animate zoom out for camera
    gsap.to(cameraRef.current.position, {
      z: originalPositionZ + 2,
      duration: 1, // Duration for camera zoom out
      ease: 'power2.out', // Easing function for smooth effect
      onComplete: () => {
        // Set target rotation to 270 degrees after zooming out
        setTargetRotation((prev) => prev + (3 * Math.PI) / 2); // Rotate 270 degrees
        rotationSpeed.current = 0.01; // Increase rotation speed after button click

        // Cycle through projects
        setWhichProject((prev) => (prev + 1) % projectTitles.length); // Cycle through project titles

        // Animate zoom back in for camera
        setTimeout(() => {
          gsap.to(cameraRef.current.position, {
            z: initialCameraPosition.current[2], // Reset camera position to the initial zoom
            duration: 1, // Duration for returning
            ease: 'power2.in', // Easing for zoom back
            onComplete: () => {
              setProjectVisible(true); // Show project divs after zooming in
              setNextButtonShow(true);
            },
          });
        }, 700); // Adjust the timing as needed
      },
    });
  };
useEffect(()=>{
  console.log(width);
  
})

const handleReadMore=()=>{
  // if(width>700){
  setIsReadMore(true)
  // }
  // else{
  //   alert('Read more is works with larger screens only')
  // }
}
  return (
    <>
    <div className='bg-black h-dvh' style={{ width: '100vw', position: 'relative', overflow: 'hidden', fontFamily: 'ropa sans' }}>
      <Canvas>
        <PerspectiveCamera makeDefault position={initialCameraPosition.current} ref={cameraRef} />
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} />
        <OrbitControls />
        {/* Render the Model and pass the target rotation value and rotation speed */}
        <Model url={modelUrl} targetRotation={targetRotation} rotationSpeed={rotationSpeed} />
      </Canvas>
      <div style={{ position: 'absolute', left: '70px', top: '7px' }}>
        <h1 className='pb-5 font-bold text-xl text-gray-200' style={{ fontFamily: 'Chakra Petch' }}>Projects</h1>
      </div>

      {/* Animated Project Divs on the left side */}
      {projectVisible && (
        <div data-aos="fade-up" className={`w-full h-full items-center flex md:flex-row flex-col ${isReadMore?'md:justify-start justify-center':'md:justify-start justify-center md:left-[70px]'} gap-10`} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)' }}>
          {/* {!isReadMore&&<div className='project-div h-5/6 md:w-5/12 w-full bg-black bg-opacity-35 rounded-lg text-white md:flex hidden items-center justify-start flex-col'>
            <Spline className='bg-transparent rounded-lg border border-gray-950' scene='https://prod.spline.design/gXXhsI-belYZRFH1/scene.splinecode' />
            <span className='pb-5 text-gray-400 '>"Hey, I am a 3D Robot Model. Move your mouse around to see the magic"</span>
          </div>} */}

          <motion.div
          data-aos="fade-up"
            key={whichProject} // Unique key for each project to trigger the animation
            className={`project-div h-5/6 border-t border-x border-gray-950 ${isReadMore?'w-full':'md:w-5/12 w-full md:ms-[72px]'} bg-black bg-opacity-35 rounded-lg text-white flex items-center justify-start flex-col`}
            initial={{ opacity: 0, scale: 0.8 }} // Start with opacity 0 and scale down
            animate={{ opacity: 1, scale: 1 }} // Animate to full opacity and original scale
            exit={{ opacity: 0, scale: 0.8 }} // Exit animation
            transition={{ duration: 2 }} // Duration of the animation
            style={{
              perspective: 1000, // Add perspective for 3D effect
              transformStyle: 'preserve-3d', // Preserve 3D transformations
            }}
          >
            <div className='w-11/12 text-xm text-gray-400 flex'>Project {whichProject + 1}</div>
            <div className={`flex h-4/6 w-11/12 ${isReadMore&&'justify-between gap-2'}`}>
              {!isReadMore?
              <img className='w-full h-full rounded-lg object-cover' src={projectTitles[whichProject].img[0]} alt='' />
            :
            <>
            {projectTitles[whichProject].img.map((imgs,index)=>(
              <img className={`${index==0?'w-6/12':'w-6/12'} h-full rounded-lg border border-gray-700`} src={imgs} alt='' />
            ))
          }
            </>
            }
            </div>
            <div className='w-11/12 pt-2 text-xl font-bold'>{projectTitles[whichProject].title}</div>
            {!isReadMore?
            <div className='w-11/12 pt-2 text-gray-400'>{projectTitles[whichProject].desc}</div>
            :
            <div className='w-11/12 pt-2 text-gray-400'>{projectTitles[whichProject].rdDesc}</div>
          }
           {(!isReadMore && width > 700) && (
  <div className='w-11/12 pt-2 text-gray-600 flex flex-row gap-1 items-center hover:text-blue-500 hover:scale-y-105'>
    <span onClick={handleReadMore}>Read more</span>
    <Icon icon='heroicons:arrow-top-right-on-square-16-solid' />
  </div>
)}

          </motion.div>
        </div>
      )}

      {/* Button to trigger model rotation */}
       {/* Button to trigger model rotation */}
       {nextButtonShow&& <div style={{ position: 'absolute', right: '0px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
        <button
          className="px-4 py-2 text-white rounded-md flex flex-row gap-1 items-center"
          onClick={handleButtonClick}
        >
        <span className='underline text-lg '>Next</span>         
         <Icon icon="material-symbols:navigate-next" className='text-4xl' />
        </button>
      </div>}
    </div> <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Text in a modal
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Duis mollis, est non commodo luctus, nisi erat porttitor ligula.
          </Typography>
        </Box>
      </Modal>

      </>


  );
}
